import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Grid, TextField, Select, MenuItem, Typography, FormControl, InputLabel } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'
import OrgSelection from '../../OrgSelection'

interface Product {
    productId: string
    productType: string
    paymentAmount: string
    paymentCurrency: string
    dataAmount: string
    dataUnit: string
}
interface FormType {
    dpId: string | null
    companyToken: string
    serviceType: string
    serviceDescription: string
    customerCountry: string
    customerZip: string
    products: Product
}
interface Props {
    selectedDp: any
    handleChangeDp: (v: any) => void
    dpoAccountInfo: any
    onChange: (form: FormType, isValid: boolean) => void
}

const COUNTRY_CURRENCY_MAP: Record<string, string[]> = {
    KE: ['KES'],
    TZ: ['TZS'],
    UG: ['UGX'],
    RW: ['RWF'],
    GH: ['GHS'],
    CI: ['XOF'],
    MW: ['MWK'],
    ZM: ['ZMW'],
    ZW: ['USD', 'ZWL']
}

const COUNTRY_OPTIONS = [
    { label: 'Ghana (GH)', value: 'GH' },
    { label: 'Ivory Coast (CI)', value: 'CI' },
    { label: 'Kenya (KE)', value: 'KE' },
    { label: 'Malawi (MW)', value: 'MW' },
    { label: 'Rwanda (RW)', value: 'RW' },
    { label: 'Tanzania (TZ)', value: 'TZ' },
    { label: 'Uganda (UG)', value: 'UG' },
    { label: 'Zambia (ZM)', value: 'ZM' },
    { label: 'Zimbabwe (ZW)', value: 'ZW' },
    { label: 'Zanzibar (TZ)', value: 'TZ' }
]

const REQUIRED_FIELDS = ['companyToken', 'serviceType', 'serviceDescription', 'customerCountry', 'customerZip']

const DEFAULT_PRODUCT: Product = {
    productId: uuidv4(),
    productType: 'topup',
    paymentAmount: '',
    paymentCurrency: '',
    dataAmount: '50',
    dataUnit: 'GB'
}

const BASIC_FIELDS = [
    { label: 'Company Token', key: 'companyToken' },
    { label: 'Service Type', key: 'serviceType' },
    { label: 'Service Description', key: 'serviceDescription' },
    { label: 'Zip Code', key: 'customerZip' }
]

const validateForm = (data: FormType) => {
    const errors: Record<string, boolean> = {}

    REQUIRED_FIELDS.forEach(key => {
        if (!data[key as keyof FormType]?.toString().trim()) {
            errors[key] = true
        }
    })
    if (data?.serviceDescription?.length > 255) errors.serviceDescription = true

    if (!/^[a-zA-Z0-9]{3,10}$/.test(data.customerZip)) {
        errors.customerZip = true
    }
    if (!data.products.paymentAmount) errors.paymentAmount = true
    if (!data.products.paymentCurrency) errors.paymentCurrency = true

    return errors
}

const FormRow = React.memo(({ label, children }: { label: string; children: React.ReactNode }) => (
    <Grid
        container
        alignItems="center"
        wrap="nowrap"
        className="starlink-serviceAccount-details-row"
    >
        <Grid item sx={{ minWidth: 160, fontWeight: 500 }}>
            {label}
        </Grid>
        <Grid item sx={{ width: 400 }}>
            {children}
        </Grid>
    </Grid>
)
)

const DpoAccountTab: React.FC<Props> = (props) => {

    const { selectedDp, handleChangeDp, dpoAccountInfo, onChange } = props
    const [form, setForm] = useState<FormType>({
        dpId: null,
        companyToken: '',
        serviceType: '',
        serviceDescription: '',
        customerCountry: '',
        customerZip: '',
        products: DEFAULT_PRODUCT
    })

    useEffect(() => {
        if (!dpoAccountInfo) return

        const updated: FormType = {
            dpId: selectedDp?.id ?? null,
            companyToken: dpoAccountInfo.companyToken ?? '',
            serviceType: dpoAccountInfo.serviceType ?? '',
            serviceDescription: dpoAccountInfo.serviceDescription ?? '',
            customerCountry: dpoAccountInfo.customerCountry ?? '',
            customerZip: dpoAccountInfo.customerZip ?? '',
            products: {
                ...DEFAULT_PRODUCT,
                ...dpoAccountInfo.products
            }
        }

        setForm(updated)
    }, [dpoAccountInfo, selectedDp])

    const errors = useMemo(() => validateForm(form), [form])

    useEffect(() => {
        onChange(form, Object.keys(errors).length === 0)
    }, [form, errors, onChange])

    const updateField = useCallback(
        (key: keyof FormType, value: string) => {
            setForm(prev => ({
                ...prev,
                [key]: value
            }))
        },
        []
    )

    const updateProduct = useCallback(
        (key: keyof Product, value: string) => {
            setForm(prev => ({
                ...prev,
                products: {
                    ...prev.products,
                    [key]: value
                }
            }))
        },
        []
    )

    return (
        <Grid container direction="column" className="starlink-serviceAccount-details-container">

            <FormRow label="Organization">
                <OrgSelection label={"Organization"} value={selectedDp} onChange={handleChangeDp} disabled={false} />
            </FormRow>

            {BASIC_FIELDS?.map(field => (
                <FormRow key={field.key} label={field.label}>
                    <TextField
                        size="small"
                        value={form[field.key as keyof FormType] ?? ''}
                        onChange={e =>
                            updateField(field.key as keyof FormType, e.target.value)
                        }
                        error={!!errors[field.key]}
                        fullWidth
                    />
                </FormRow>
            ))}

            <FormRow label="Country">
                <Select
                    size="small"
                    value={form.customerCountry}
                    onChange={e => {
                        const selectedCountry = e.target.value as string
                        const currencies = COUNTRY_CURRENCY_MAP[selectedCountry] || []

                        setForm(prev => ({
                            ...prev,
                            customerCountry: selectedCountry,
                            products: {
                                ...prev.products,
                                paymentCurrency: currencies.length === 1 ? currencies[0] : ''
                            }
                        }))
                    }}
                    fullWidth
                    error={!!errors.customerCountry}
                >
                    {COUNTRY_OPTIONS.map(option => (
                        <MenuItem key={option.label} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormRow>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Payment Items
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={3}>
                    <Typography fontWeight={300}>
                        Product
                    </Typography>
                    <Typography color="text.secondary">
                        TopUp
                    </Typography>
                </Grid>
                <Grid item xs={3}>
                    <Typography fontWeight={300}>
                        Size
                    </Typography>
                    <Typography color="text.secondary">
                        {form.products.dataAmount} GB
                    </Typography>
                </Grid>


                <Grid item xs={3}>
                    <TextField
                        label="Price"
                        size="small"
                        type="text"
                        value={form.products.paymentAmount}
                        onChange={e => {
                            const raw = e.target.value.replace(/[^0-9.]/g, '')

                            if (raw === '') {
                                updateProduct('paymentAmount', '')
                                return
                            }

                            if (raw.startsWith('0')) return

                            const numericValue = Number(raw)

                            if (numericValue <= 0 || numericValue > 10000) return

                            updateProduct('paymentAmount', raw)
                        }}

                        fullWidth
                    />
                </Grid>

                <Grid item xs={3}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Currency</InputLabel>
                        <Select
                            label="Currency"
                            value={form.products.paymentCurrency}
                            onChange={e => updateProduct('paymentCurrency', e.target.value)}
                        >
                            {(COUNTRY_CURRENCY_MAP[form.customerCountry] || []).map(currency => (
                                <MenuItem key={currency} value={currency}>
                                    {currency}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default React.memo(DpoAccountTab)